import { intersection } from "lodash-es";
import objectHash from "object-hash";
import { useEffect, useState } from "react";

import {
  emptyCluster,
  emptySettings,
  setClusterForm,
  setSettingsForm,
} from "../../../slices/settings";
import { useDispatch, useSelector } from "../../../store";
import { useGetSettingsQuery } from "../../../store/enhancedApi";
import {
  useGetClusterQuery,
  useUpdateClusterMutation,
  useUpdateSettingsMutation,
} from "../../../store/pomeriumApi";

// these fields will disable the save button if they have an error
const requiredFieldsWithNoError = ["sshHostKeys", "sshUserCaKey", "sshAddress"];

export const useSettingsFunctions = () => {
  const {
    form,
    formErrors = {},
    settingsHash,
    clusterForm,
    clusterHash,
  } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const [refresh, setRefresh] = useState(settingsHash);
  const [clusterRefresh, setClusterRefresh] = useState(clusterHash);
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateSettingsMutation();
  const [updateCluster] = useUpdateClusterMutation();

  const {
    data: cluster,
    error: clusterError,
    isLoading: isClusterLoading,
  } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );

  const { data: settings, error: settingsError } = useGetSettingsQuery(
    {
      organizationId,
      clusterId,
    },
    {
      // Skip settings query if:
      // 1. Required IDs are missing, OR
      // 2. Cluster query is still loading to avoid race condition
      skip: !organizationId || !clusterId || isClusterLoading,
      clusterFlavor: cluster?.flavor,
    },
  );

  useEffect(() => {
    dispatch(setSettingsForm(settings || emptySettings));
  }, [JSON.stringify(settings || {})]);

  useEffect(() => {
    dispatch(setClusterForm(cluster || emptyCluster));
  }, [JSON.stringify(cluster || {})]);

  useEffect(() => {
    setRefresh(settingsHash);
    setClusterRefresh(clusterHash);
  }, [settingsHash, refresh, clusterHash]);

  const handleSubmit = async () => {
    if (objectHash(form) !== settingsHash) {
      const updated = await updateSettings({
        organizationId,
        clusterId,
        settingsProperties: form,
      }).unwrap();
      dispatch(setSettingsForm(updated));
    }
    if (objectHash(clusterForm) !== clusterHash) {
      const newCluster = await updateCluster({
        organizationId,
        clusterId,
        clusterProperties: clusterForm,
      }).unwrap();
      dispatch(setClusterForm(newCluster));
    }
  };

  const handleClickDiscard = async () => {
    setRefresh("refresh");
    setClusterRefresh("refresh");
    dispatch(setSettingsForm(settings || emptySettings));
    dispatch(setClusterForm(cluster || emptyCluster));
  };

  const shouldSave = (): boolean => {
    return (
      (objectHash(form) !== settingsHash ||
        objectHash(clusterForm) !== clusterHash) &&
      intersection(Object.keys(formErrors), requiredFieldsWithNoError).length ==
        0
    );
  };

  const allowCancel =
    objectHash(form) !== settingsHash ||
    objectHash(clusterForm) !== clusterHash;

  return {
    refresh,
    clusterRefresh,
    clusterError,
    settingsError,
    settingsHash,
    allowCancel,
    handleSubmit,
    shouldSave,
    handleClickDiscard,
    isUpdating,
    cluster,
  };
};
